import { useMemo, useState, type FormEvent } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { RequireApproved } from "@/components/require-approved"
import {
  bumpEndAfterStart,
  defaultEventWindow,
  formatHackathonPreviewDate,
  hackathonDraftToArgs,
  hackathonWindowError,
  DEFAULT_HACKATHON_CITY,
  type HackathonImageUpload,
} from "@/lib/hackathon-form"
import {
  imageFileValidator,
  useStorageUpload,
} from "@/hooks/use-storage-upload"
import { usePendingAction } from "@/hooks/use-pending-action"
import { ImagePlus, Link2, Users } from "lucide-react"

export const Route = createFileRoute("/recruit/hackathons_/new")({
  component: NewHackathonPage,
})

const MAX_POSTER_BYTES = 5 * 1024 * 1024

function NewHackathonPage() {
  return (
    <RequireApproved capability="manageHackathons">
      <NewHackathonForm />
    </RequireApproved>
  )
}

function NewHackathonForm() {
  const defaults = useMemo(() => defaultEventWindow(), [])
  const create = useMutation(api.hackathons.create)
  const jobs = useQuery(api.jobs.listMine)
  const navigate = useNavigate()

  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [venue, setVenue] = useState("")
  const [address, setAddress] = useState("")
  const [city, setCity] = useState(DEFAULT_HACKATHON_CITY)
  const [startsAt, setStartsAt] = useState(defaults.startsAt)
  const [endsAt, setEndsAt] = useState(defaults.endsAt)
  const [capacity, setCapacity] = useState("")
  const [linkedJobId, setLinkedJobId] = useState<string>("none")
  const [image, setImage] = useState<HackathonImageUpload | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const { pending: busy, run: runCreate } = usePendingAction()

  const timingError = hackathonWindowError(startsAt, endsAt, {
    requireFuture: true,
  })

  const {
    fileInputRef: fileRef,
    uploading,
    upload: uploadPoster,
  } = useStorageUpload({
    purpose: "hackathon_image",
    validate: imageFileValidator(MAX_POSTER_BYTES),
    onUploaded: (ref, file) => {
      setImage(ref)
      setImagePreviewUrl(URL.createObjectURL(file))
    },
    successMessage: "Poster added",
  })

  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    venue.trim().length > 0 &&
    address.trim().length > 0 &&
    !timingError &&
    !busy &&
    !uploading

  const previewDate = formatHackathonPreviewDate(startsAt)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    await runCreate(async () => {
      const { image: _draftImage, ...draftArgs } = hackathonDraftToArgs({
        title,
        description,
        venue,
        address,
        city,
        startsAt,
        endsAt,
        capacity,
        imageUrl: imagePreviewUrl,
        imageUpload: image ?? undefined,
      })
      const id = await create({
        ...draftArgs,
        image: image ?? undefined,
        linkedJobId:
          linkedJobId !== "none" ? (linkedJobId as Id<"jobs">) : undefined,
      })
      toast.success("Draft saved — pay ₹3,000 when you're ready to publish")
      void navigate({
        to: "/recruit/hackathons/$hackathonId",
        params: { hackathonId: id },
        search: { pay: true },
      })
    })
  }

  return (
    <div className="mkt-event-create">
      <header className="mkt-event-create__header">
        <div>
          <Link
            to="/recruit/hackathons"
            className="mkt-event-create__back"
          >
            ← Your hackathons
          </Link>
          <h1 className="mkt-page-title">New hackathon</h1>
          <p className="mkt-page-sub">
            Drafts are free. You pay ₹3,000 only when you publish to the community board.
          </p>
        </div>
      </header>

      <form className="mkt-event-create__stage" onSubmit={onSubmit}>
        <aside className="mkt-event-create__media">
          <p className="mkt-event-create__section-label">Poster</p>
          <button
            type="button"
            className="mkt-event-create__poster"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Upload hackathon poster"
          >
            {imagePreviewUrl ? (
              <img src={imagePreviewUrl} alt="" />
            ) : (
              <span className="mkt-event-create__poster-empty">
                <ImagePlus className="size-7" aria-hidden />
                <span>{uploading ? "Uploading…" : "Add poster"}</span>
                <span className="mkt-event-create__poster-hint">Optional · 5 MB max</span>
              </span>
            )}
            <span className="mkt-event-create__poster-badge" aria-hidden>
              <ImagePlus className="size-4" />
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void uploadPoster(file)
            }}
          />
          {imagePreviewUrl && (
            <button
              type="button"
              className="mkt-event-create__clear"
              onClick={() => {
                setImage(null)
                setImagePreviewUrl(null)
              }}
            >
              Remove poster
            </button>
          )}

          <section className="mkt-event-create__preview" aria-live="polite">
            <p className="mkt-event-create__section-label">List preview</p>
            <div className="mkt-event-create__preview-card">
              <div className="mkt-event-create__preview-top">
                <h2 className="mkt-event-create__preview-title">
                  {title.trim() || "Hackathon name"}
                </h2>
                <span className="mkt-chip mkt-chip--outline">Draft</span>
              </div>
              <p className="mkt-event-create__preview-meta">
                {[venue.trim(), city.trim()].filter(Boolean).join(" · ")}
                {previewDate ? ` · ${previewDate}` : ""}
              </p>
              <span className="mkt-event-create__preview-cue">Manage →</span>
            </div>
          </section>
        </aside>

        <div className="mkt-event-create__form">
          <section className="mkt-event-create__section">
            <p className="mkt-event-create__section-label">1. Event name</p>
            <input
              className="mkt-event-create__title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. HSR Build Sprint · Spring 2026"
              required
              aria-label="Hackathon name"
              autoFocus
            />
          </section>

          <section className="mkt-event-create__section">
            <p className="mkt-event-create__section-label">2. When</p>
            <div className="mkt-event-create__datetime-grid">
              <div className="mkt-event-create__field-wrap">
                <label className="mkt-event-create__label" htmlFor="hc-start">
                  Starts
                </label>
                <Input
                  id="hc-start"
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => {
                    const nextStart = e.target.value
                    setStartsAt(nextStart)
                    setEndsAt((current) => bumpEndAfterStart(nextStart, current))
                  }}
                  required
                  className="mkt-event-create__field"
                />
              </div>
              <div className="mkt-event-create__field-wrap">
                <label className="mkt-event-create__label" htmlFor="hc-end">
                  Ends
                </label>
                <Input
                  id="hc-end"
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  required
                  className="mkt-event-create__field"
                  aria-invalid={timingError ? true : undefined}
                />
              </div>
            </div>
            {timingError && (
              <p className="mkt-event-create__error">{timingError}</p>
            )}
          </section>

          <section className="mkt-event-create__section">
            <p className="mkt-event-create__section-label">3. Where</p>
            <div className="mkt-event-create__field-stack">
              <div className="mkt-event-create__field-wrap">
                <label className="mkt-event-create__label" htmlFor="hc-venue">
                  Venue
                </label>
                <Input
                  id="hc-venue"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="e.g. WeWork HSR, Koramangala Indoor Stadium"
                  required
                  className="mkt-event-create__field"
                />
              </div>
              <div className="mkt-event-create__field-wrap">
                <label className="mkt-event-create__label" htmlFor="hc-address">
                  Street address
                </label>
                <Input
                  id="hc-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Building, street, landmark"
                  required
                  className="mkt-event-create__field"
                />
              </div>
              <div className="mkt-event-create__field-wrap">
                <label className="mkt-event-create__label" htmlFor="hc-city">
                  City / area
                </label>
                <Input
                  id="hc-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder={DEFAULT_HACKATHON_CITY}
                  className="mkt-event-create__field"
                />
              </div>
            </div>
          </section>

          <section className="mkt-event-create__section">
            <p className="mkt-event-create__section-label">4. About</p>
            <Textarea
              id="hc-about"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What builders will ship, mentors on site, prizes, who should join…"
              required
              rows={5}
              className="mkt-event-create__field mkt-event-create__textarea"
            />
            <p className="mkt-event-create__char-count">
              {description.trim().length} characters
            </p>
          </section>

          <section className="mkt-event-create__section">
            <p className="mkt-event-create__section-label">5. Options</p>
            <div className="mkt-event-create__options">
              <div className="mkt-event-create__option">
                <div className="mkt-event-create__option-left">
                  <Users className="size-4" aria-hidden />
                  <span>Capacity</span>
                </div>
                <Input
                  type="number"
                  min={1}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  placeholder="Unlimited"
                  className="mkt-event-create__option-input"
                  aria-label="Capacity"
                />
              </div>
              <div className="mkt-event-create__option">
                <div className="mkt-event-create__option-left">
                  <Link2 className="size-4" aria-hidden />
                  <span>Link a job</span>
                </div>
                <Select value={linkedJobId} onValueChange={setLinkedJobId}>
                  <SelectTrigger className="mkt-event-create__option-select">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked job</SelectItem>
                    {jobs?.map((j) => (
                      <SelectItem key={j._id} value={j._id}>
                        {j.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mkt-event-create__option mkt-event-create__option--static">
                <div className="mkt-event-create__option-left">
                  <span className="mkt-event-create__rupee" aria-hidden>
                    ₹
                  </span>
                  <span>Publish fee</span>
                </div>
                <span className="mkt-event-create__option-value">₹3,000</span>
              </div>
            </div>
          </section>

          <div className="mkt-event-create__footer">
            <p className="mkt-event-create__footer-copy">
              Saves as draft · publish anytime from the manage page
            </p>
            <button
              type="submit"
              className="home-wf-btn home-wf-btn--lime mkt-event-create__submit"
              disabled={!canSubmit}
            >
              {busy ? "Saving…" : "Save draft"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
